import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  Container
} from '@mui/material';
import { 
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const ForbiddenPage = ({ resource = 'resource' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Function to determine back navigation based on current path
  const getBackNavigation = () => {
    const path = location.pathname;

    // Analyze current path and provide appropriate back navigation
    if (path.includes('/leads/')) {
      return {
        path: '/leads',
        text: 'Go Back to Leads'
      };
    } else if (path.includes('/calls/')) {
      return {
        path: '/calls',
        text: 'Go Back to Calls'
      };
    } else if (path.includes('/agents/')) {
      return {
        path: '/agents',
        text: 'Go Back to Agents'
      };
    } else if (path.includes('/reports/')) {
      return {
        path: '/reports',
        text: 'Go Back to Reports'
      };
    } else if (path.includes('/settings/')) {
      return {
        path: '/settings',
        text: 'Go Back to Settings'
      };
    } else {
      // Default fallback to home
      return {
        path: '/',
        text: 'Go to Home'
      };
    }
  };

  const backNavigation = getBackNavigation();

  const handleBackClick = () => {
    navigate(backNavigation.path);
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            maxWidth: 500,
            width: '100%'
          }}
        >
          {/* Block Icon */}
          <Box sx={{ mb: 3 }}>
            <BlockIcon 
              sx={{ 
                fontSize: 80, 
                color: 'error.main',
                opacity: 0.8
              }} 
            />
          </Box>

          {/* 403 Number */}
          <Typography
            variant="h1"
            sx={{
              fontSize: '6rem',
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1,
              mb: 2,
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            403
          </Typography>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 2
            }}
          >
            Access Not Allowed
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 4,
              lineHeight: 1.6,
              fontSize: '1.1rem'
            }}
          >
            You do not have permission to access this {resource}.
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Back Button */}
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {backNavigation.text}
            </Button>

            {/* Home Button */}
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={handleHomeClick}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Go to Home
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForbiddenPage;
